import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "@reduxjs/toolkit";
import styles from "./style.module.scss";
import {
  disableDriver,
  enableDriver,
  disableSession,
  enableSession,
  resetFilters,
  setLapTimeFilter,
  setLoadingGraphs,
} from "../../../state/performanceMetricsReducer";
import { RootState } from "../../../state";
import { isEqual } from "lodash";

interface MetricsFiltersBarProps {
  metricNames?: string[];
}

function MetricsFiltersBar({ metricNames = [] }: MetricsFiltersBarProps) {
  const dispatch = useDispatch();
  const [lapTimeMin, setLapTimeMin] = useState("");
  const [lapTimeMax, setLapTimeMax] = useState("");
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);

  const dispatchFilterChange = (action: AnyAction) => {
    dispatch(setLoadingGraphs(true));
    setTimeout(() => {
      dispatch(action);
    }, 0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dispatch(setLoadingGraphs(false));
      });
    });
  };

  const isViewingLoadedData = useSelector(
    (state: RootState) => state.performanceMetrics?.isViewingLoadedData,
  );

  const performanceMetricsData = useSelector(
    (state: RootState) =>
      isViewingLoadedData
        ? state.performanceMetrics?.loadedPerformanceMetricsData
        : state.performanceMetrics?.livePerformanceMetricsData,
    isEqual,
  );

  const filters = useSelector(
    (state: RootState) => state.performanceMetrics?.filters,
    isEqual,
  );

  const performanceMetricsDrivers = useMemo(() => {
    return performanceMetricsData?.MetricLaps
      ? [
          ...new Set(
            performanceMetricsData.MetricLaps.map((lap) => lap.driver),
          ),
        ].filter((driver): driver is string => driver !== null)
      : [];
  }, [performanceMetricsData?.MetricLaps]);

  const performanceMetricsSessions = useMemo(() => {
    return performanceMetricsData?.MetricLaps
      ? [
          ...new Set(
            performanceMetricsData.MetricLaps.map(
              (lap) => lap.description?.[0]?.split(" ")[1],
            ),
          ),
        ].filter((session): session is string => session !== undefined)
      : [];
  }, [performanceMetricsData?.MetricLaps]);

  useEffect(() => {
    const enabledDrivers = performanceMetricsDrivers.filter((driver) => {
      return !filters?.disabledDrivers?.includes(driver);
    });
    setSelectedDrivers(enabledDrivers);
  }, [performanceMetricsDrivers, filters?.disabledDrivers]);

  useEffect(() => {
    const enabledSessions = performanceMetricsSessions.filter((session) => {
      return !filters?.disabledSessions?.includes(session);
    });
    setSelectedSessions(enabledSessions);
  }, [performanceMetricsSessions, filters?.disabledSessions]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <span>
          {"Laptime Min: "}
          <input
            className={styles.filterInput}
            value={lapTimeMin}
            type="number"
            onChange={(e) => {
              setLapTimeMin(e.target.value);
              dispatchFilterChange(
                setLapTimeFilter({
                  min: e.target.value !== "" ? e.target.value : -Infinity,
                  max: lapTimeMax !== "" ? lapTimeMax : Infinity,
                }),
              );
            }}
          ></input>
          {" Max: "}
          <input
            className={styles.filterInput}
            value={lapTimeMax}
            type="number"
            onChange={(e) => {
              setLapTimeMax(e.target.value);
              dispatchFilterChange(
                setLapTimeFilter({
                  min: lapTimeMin !== "" ? lapTimeMin : -Infinity,
                  max: e.target.value !== "" ? e.target.value : Infinity,
                }),
              );
            }}
          ></input>
          {"s"}
        </span>
        <button
          className={styles.resetButton}
          onClick={() => {
            dispatchFilterChange(resetFilters());
            setLapTimeMin("");
            setLapTimeMax("");
          }}
        >
          Reset Filters and Disabled Points
        </button>
        <span>Drivers:</span>
        {performanceMetricsDrivers.map(
          (driver) =>
            driver && (
              <label key={driver} className={styles.checkbox} htmlFor={driver}>
                <input
                  type="checkbox"
                  checked={selectedDrivers.includes(driver)}
                  id={driver}
                  name={driver}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setSelectedDrivers((prev) =>
                      isChecked
                        ? [...prev, driver]
                        : prev.filter((d) => d !== driver),
                    );
                    if (isChecked) {
                      dispatchFilterChange(enableDriver(driver));
                    } else {
                      dispatchFilterChange(disableDriver(driver));
                    }
                  }}
                />
                {driver}
              </label>
            ),
        )}
        <span>Sessions:</span>
        {performanceMetricsSessions.map(
          (session) =>
            session && (
              <label
                key={session}
                className={styles.checkbox}
                htmlFor={`session-${session}`}
              >
                <input
                  type="checkbox"
                  checked={selectedSessions.includes(session)}
                  id={`session-${session}`}
                  name={session}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setSelectedSessions((prev) =>
                      isChecked
                        ? [...prev, session]
                        : prev.filter((s) => s !== session),
                    );
                    if (isChecked) {
                      dispatchFilterChange(enableSession(session));
                    } else {
                      dispatchFilterChange(disableSession(session));
                    }
                  }}
                />
                {session}
              </label>
            ),
        )}
      </div>
      <div className={styles.tableOfContents}>
        {metricNames.length > 0 && (
          <div className={styles.tableOfContents}>
            {metricNames.map((name) => (
              <a
                key={name}
                href={`#${name.replace(/\s+/g, "-").toLowerCase()}`}
                className={styles.contentsLink}
              >
                {name}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MetricsFiltersBar;
